import { OpButton } from 'components/customAntd/DLS/OpButton/OpButton';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'store/store';
import { hasPermission } from 'utils/utils';
import { OpSpace } from 'components/customAntd/DLS/OpSpace/OpSpace';
import { notification } from 'antd';
import { useConfirmModal } from 'utils/customHooks/useConfirmModal';
import VisitorsEditModal from './VisitorsEditModal';
import { deleteRequest, patchRequest } from 'api/apiClient';
import { fetchVisits } from 'store/slices/visitsSlice';
import { searchVisitors, searchVisitorsToday } from 'store/slices/visitorSearchSlice';
import { useTranslation } from 'react-i18next';
import i18n from 'i18n/i18n';

interface VisitorsDrawerHeaderProps {
    onClose: () => void;
    setLoading: (loading: boolean) => void;
    loading: boolean;
}

const VisitorsDrawerHeader: React.FC<VisitorsDrawerHeaderProps> = ({ onClose, setLoading, loading }) => {
    const dispatch: AppDispatch = useDispatch();
    const orgId = useSelector((state: RootState) => state.globalOrg.globalOrgId);
    const globalUserId = useSelector((state: RootState) => state.users.globalUser?.id);
    const tokenScopeList = useSelector((state: RootState) => state.auth.auth.data[0]?.tokenScopeList || []);
    const { selectedVisit } = useSelector((state: RootState) => state.visits);
    const globalLocationId = useSelector((state: RootState) => state.locations.globalLocation?.id);
    const lastSearchParams = useSelector((state: RootState) => state.visitorSearch.lastSearchParams);

    const { t } = useTranslation();
    const confirmModal = useConfirmModal();

    const hasVisitorsWrite = hasPermission(tokenScopeList, orgId, 'o', 'visitor:w');
    const hasAllvisitorsWrite = hasPermission(tokenScopeList, orgId, 'o', 'allvisitors:w');
    const [isVisitorsEditModalOpen, setIsVisitorsEditModalOpen] = useState(false);

    const onDeleteClick = () => {
        confirmModal({
            title: t('visitors.header.confirmDelete.title'),
            content: t('visitors.header.confirmDelete.message'),
            okText: t('visitors.header.confirmDelete.ok'),
            cancelText: t('visitors.header.confirmDelete.cancel'),
            onOk: async () => {
                try {
                    await deleteRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}/msiAltaGuestPass`);
                    await patchRequest(`/orgs/${orgId}/visitor/${selectedVisit?.id}?language=${i18n.language.split('-')[0]}`, { status: 0 });

                    await dispatch(fetchVisits({ orgId }));
                    await dispatch(searchVisitorsToday({ orgId }));
                    if (Object.keys(lastSearchParams).length !== 0) {
                        await dispatch(searchVisitors({
                            ...lastSearchParams,
                            orgId,
                            siteId: lastSearchParams.siteId || globalLocationId!
                        }));
                    }

                    notification.success({
                        message: t('notification.success'),
                        description: t('visitors.header.notification.deleteSuccess'),
                        placement: 'bottomRight',
                    });

                    onClose();
                } catch (error) {
                    console.log(t('Failed to delete visit.'));
                }
            },
        });
    };

    return (
        <>
            <OpSpace>
                {
                    (hasAllvisitorsWrite || (hasVisitorsWrite && globalUserId === selectedVisit?.host.userId)) && (
                        <>
                            {selectedVisit?.visitStatus.id === 1 && selectedVisit?.visitors.every(visitor => visitor.status === 1) && (
                                <OpButton type="primary" danger ghost onClick={onDeleteClick} disabled={loading}>
                                    {t('visitors.header.delete')}
                                </OpButton>
                            )}
                            <OpButton type="primary" ghost onClick={() => setIsVisitorsEditModalOpen(true)} disabled={loading}>
                                {t('visitors.header.edit')}
                            </OpButton>
                        </>
                    )}
            </OpSpace>

            {isVisitorsEditModalOpen && (
                <VisitorsEditModal
                    open={isVisitorsEditModalOpen}
                    onClose={() => setIsVisitorsEditModalOpen(false)}
                    setLoading={setLoading}
                />
            )}
        </>
    );
};

export default VisitorsDrawerHeader;
